import animation_mouse_scroll_down from '@/assets/animations/animation_mouse_scroll_down.svg?raw';
import animation_mouse_scroll_up from '@/assets/animations/animation_mouse_scroll_up.svg?raw';
import template_animation_mouse_left_click from '@/assets/animations/templates/template_animation_mouse_left_click.svg?raw';
import template_animation_mouse_left_double_click from '@/assets/animations/templates/template_animation_mouse_left_double_click.svg?raw';
import template_animation_mouse_right_click from '@/assets/animations/templates/template_animation_mouse_right_click.svg?raw';
import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { use_effect } from '@/hooks/_base/use_effect';
import { set_interval, set_timeout } from '@/utils/utils_client';
import { IMouseData } from '@shared/dto/hint_shortcuts/i_mouse_data';
import { Fragment, useState } from 'react';

interface Props {
  keys: string[];
  mouse_data: IMouseData;
  index: number;
  keys_have_same_animation_delay: boolean;
  disable_keyboard_animation?: boolean;
}

export function ViewHintShortcutIcons({ keys, mouse_data, index, keys_have_same_animation_delay, disable_keyboard_animation = false }: Props) {
  const [is_pressed, set_is_pressed] = useState(false);

  const [icon_mouse_left_raw, set_icon_mouse_left_raw] = useState('');
  const [icon_mouse_right_raw, set_icon_mouse_right_raw] = useState('');
  const [icon_mouse_left_double_raw, set_icon_mouse_left_double_raw] = useState('');

  const {
    INTERVAL_ANIMATION_DELAY_HINT_SHORTCUT_KEY_CLICK,
    TIMEOUT_ANIMATION_DURARION_HINT_SHORTCUT_KEY_CLICK,
    TIMEOUT_ANIMATION_DELAY_HINT_SHORTCUT_LIST_ITEM,
  } = CONSTANTS_TIME__client;

  /*
   * EFFECTS
   */

  use_effect(() => {
    const delay = keys_have_same_animation_delay ? 0 : index * TIMEOUT_ANIMATION_DELAY_HINT_SHORTCUT_LIST_ITEM;

    let timeout_id = NaN;
    let interval_id = NaN;

    let timeout_start_id = set_timeout(() => {
      let icon_mouse_left_raw_new = fill_icon_template(template_animation_mouse_left_click, delay);
      set_icon_mouse_left_raw(icon_mouse_left_raw_new);

      let icon_mouse_left_right_new = fill_icon_template(template_animation_mouse_right_click, delay);
      set_icon_mouse_right_raw(icon_mouse_left_right_new);

      let icon_mouse_left_left_double_new = fill_icon_template(template_animation_mouse_left_double_click, delay);
      set_icon_mouse_left_double_raw(icon_mouse_left_left_double_new);

      if (disable_keyboard_animation) return;

      timeout_id = animation_cycle();

      interval_id = set_interval(animation_cycle, INTERVAL_ANIMATION_DELAY_HINT_SHORTCUT_KEY_CLICK);
    }, delay);

    return () => {
      clearTimeout(timeout_id);
      clearInterval(interval_id);
      clearTimeout(timeout_start_id);
    };
  }, [mouse_data, index, keys_have_same_animation_delay, disable_keyboard_animation]);

  /*
   * METHODS
   */

  function fill_icon_template(icon_template: string, delay: number) {
    return icon_template
      .replace(CONSTANTS__client.STRING_VARIABLE_DURATION, `${INTERVAL_ANIMATION_DELAY_HINT_SHORTCUT_KEY_CLICK / 1000}`)
      .replace(CONSTANTS__client.STRING_VARIABLE_DELAY, `${Math.floor(delay / 1000)}`);
  }

  function animation_cycle() {
    set_is_pressed(true);

    const timeout_id = set_timeout(() => set_is_pressed(false), TIMEOUT_ANIMATION_DURARION_HINT_SHORTCUT_KEY_CLICK);

    return timeout_id;
  }

  /*
   * RENDERS
   */

  function render_plus() {
    if (keys.length === 0) return null;

    return <span className="text-page-foreground text-sm">+</span>;
  }

  function render_icon(icon: string) {
    return (
      <>
        {render_plus()}
        <div dangerouslySetInnerHTML={{ __html: icon }} className="opacity-90 text-component" style={{ width: `${20}px`, height: `${27}px` }} />
      </>
    );
  }

  function render_mouse_click() {
    if (mouse_data.is_mouse_left) return render_icon(icon_mouse_left_raw);

    if (mouse_data.is_mouse_left_double_click) return render_icon(icon_mouse_left_double_raw);

    if (mouse_data.is_mouse_right) return render_icon(icon_mouse_right_raw);

    if (mouse_data.is_scroll_up) return render_icon(animation_mouse_scroll_up);

    if (mouse_data.is_scroll_down) return render_icon(animation_mouse_scroll_down);

    return null;
  }

  function render_keyboard_keys() {
    return keys.map((key, index) => (
      <Fragment key={key}>
        <div
          className={`px-[8px] h-[22px] flex items-center justify-center border border-component-border rounded-[5px] bg-component text-page-foreground text-[12px] font-bold font-mono transition-all duration-500 
               ${is_pressed && !disable_keyboard_animation ? ' border-b-[1px] translate-y-[3px] h-[19px]' : ' border-b-[4px] h-[22px]'}`}
        >
          {key.toLowerCase()}
        </div>
        {index !== keys.length - 1 && <span className="text-page-foreground text-sm">+</span>}
      </Fragment>
    ));
  }

  /*
   * HTML
   */

  return (
    <div className="flex items-center justify-center gap-[6px]">
      {render_keyboard_keys()}
      {render_mouse_click()}
    </div>
  );
}
